import { ReactNode } from "react";
import { Layer as DeckGlLayer } from "@deck.gl/core";

import TimeRange from "@/types/time-range";
import { Dimension, DimensionValueSet } from "@/lib/dimensions";
import { getCacheKeyForDimensionValueSet } from "@/lib/dimensions/helpers";
import { Layer } from "@/lib/map-layers/layer";
import { LoadingManager } from "@/lib/map-layers/loading-manager";
import { LayerSetting, LayerSettingValueSet } from "@/lib/map-layers/layer-settings";

import { queue } from "./queue";
import {
  TiledDeckGlLayer,
  BoundingBox,
  GetTileImageProps,
  TileChangeSet,
  TileInfo,
} from "./tiled-deck-gl-layer";
import { BaseTileImageLoader } from "./base-tile-image-loader";
import { TileImageStore } from "./tile-image-store";
import { TileImageTaskStore } from "./tile-image-task-store";
import { TileLoadFailureTracker } from "./tile-load-failure-tracker";

const PRIORITY_CURRENT_VIEW = 0;
const PRIORITY_PRELOAD = PRIORITY_CURRENT_VIEW + 1;

export type BaseTiledLayerAttributes = {
  tileImageLoader: BaseTileImageLoader;
};

export abstract class BaseTiledLayer implements Layer {
  public loadingManager = new LoadingManager();
  public currentViewLoadingManager = new LoadingManager();

  private tileImageLoader: BaseTileImageLoader;
  private tileImageStore = new TileImageStore();
  private tileImageTaskStore = new TileImageTaskStore();
  private loadFailureTracker = new TileLoadFailureTracker();

  private tiles = [] as TileInfo[];

  protected get queue() {
    return queue;
  }

  constructor(attributes: BaseTiledLayerAttributes) {
    this.tileImageLoader = attributes.tileImageLoader;
  }

  abstract get id(): string;
  abstract get settings(): LayerSetting[];
  abstract get dimensions(): Dimension[] | undefined;
  abstract initialize(timeRange: TimeRange): Promise<unknown>;
  abstract getTitle(dimensionValues: DimensionValueSet): string;
  abstract isActive(dimensionValues: DimensionValueSet): boolean;
  abstract isForecast(dimensionValues: DimensionValueSet): boolean;
  abstract getRelevantDimensionValues(values: DimensionValueSet): DimensionValueSet;

  abstract get opacity(): number;
  abstract get tileSize(): number;
  abstract get minZoom(): number;
  abstract get maxZoom(): number;
  abstract get extent(): BoundingBox | undefined;

  abstract renderLegend(
    dimensionValues: DimensionValueSet,
    settingValues: LayerSettingValueSet,
  ): ReactNode;

  isLoaded(dimensionValues: DimensionValueSet) {
    return this.tiles.every((tile) => {
      return this.tileImageStore.hasTileImage({
        tile,
        dimensionValues: this.getRelevantDimensionValues(dimensionValues),
      });
    });
  }

  hasLoadError(dimensionValues: DimensionValueSet) {
    return this.loadFailureTracker.hasLoadError(
      this.getRelevantDimensionValues(dimensionValues),
    );
  }

  hasPartialLoadErrors() {
    return this.loadFailureTracker.hasPartialLoadErrors();
  }

  clearLoadFailures() {
    this.loadFailureTracker.clear();
  }

  async preload(
    dimensionValues: DimensionValueSet,
    priority: number,
    signal?: AbortSignal,
  ) {
    const relevantDimensionValues = this.getRelevantDimensionValues(dimensionValues);
    await Promise.all(
      this.tiles.map((tile) => {
        if (
          this.tileImageStore.hasTileImage({
            tile,
            dimensionValues: relevantDimensionValues,
          })
        )
          return;
        return this.loadAndStoreTileImage(
          relevantDimensionValues,
          { tile, signal },
          PRIORITY_PRELOAD + priority,
        );
      }),
    );
  }

  renderLayer(currentDimensionValues: DimensionValueSet): DeckGlLayer | undefined {
    const relevantCurrentDimensionValues =
      this.getRelevantDimensionValues(currentDimensionValues);

    return new TiledDeckGlLayer({
      id: `tiled-wms-layer-${this.id}`,
      visible: this.isActive(currentDimensionValues),

      tileSize: this.tileSize,
      slot: "middle",
      opacity: this.opacity,

      extent: this.extent,
      minZoom: this.minZoom,
      maxZoom: this.maxZoom,

      updateTriggers: {
        getTileImage: {
          relevantCurrentDimensionValues: getCacheKeyForDimensionValueSet(
            relevantCurrentDimensionValues,
          ),
        },
      },

      onTilesChanged: this.onTilesChanged.bind(this),

      getTileImage: async (getTileImageProps) => {
        return await this.getOrLoadTileImage(
          relevantCurrentDimensionValues,
          getTileImageProps,
        );
      },
    });
  }

  private async getOrLoadTileImage(
    dimensionValues: DimensionValueSet,
    props: GetTileImageProps,
  ) {
    const stored = this.tileImageStore.getTileImage({ tile: props.tile, dimensionValues });
    if (stored) return stored;

    const priority = this.isActive(dimensionValues)
      ? PRIORITY_CURRENT_VIEW
      : PRIORITY_PRELOAD;
    return await this.loadAndStoreTileImage(dimensionValues, props, priority);
  }

  private async loadAndStoreTileImage(
    dimensionValues: DimensionValueSet,
    props: GetTileImageProps,
    priority: number,
  ) {
    try {
      const cachedTileImage = await this.loadCachedTileImage(
        dimensionValues,
        props,
        priority,
      );
      const image =
        cachedTileImage || (await this.loadTileImage(dimensionValues, props, priority));
      this.tileImageStore.storeTileImage(image, { tile: props.tile, dimensionValues });
      return image;
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        // noop
        return undefined;
      }

      this.loadFailureTracker.markFailed(dimensionValues);
      throw error;
    }
  }

  private async loadTileImage(
    dimensionValues: DimensionValueSet,
    props: GetTileImageProps,
    priority: number,
  ) {
    const pendingTask = this.tileImageTaskStore.findPendingTask({
      tile: props.tile,
      dimensionValues,
    });
    if (pendingTask) {
      return await pendingTask.promise;
    }

    const task = this.queue.enqueue({
      priority,
      signal: props.signal,
      run: ({ signal }) => {
        return this.tileImageLoader.loadTileImage(dimensionValues, { ...props, signal });
      },
    });

    if (priority == PRIORITY_CURRENT_VIEW)
      this.currentViewLoadingManager.register(task.promise);
    this.loadingManager.register(task.promise);

    this.tileImageTaskStore.registerTask(task, { tile: props.tile, dimensionValues });

    return await task.promise;
  }

  private async loadCachedTileImage(
    dimensionValues: DimensionValueSet,
    props: GetTileImageProps,
    priority: number,
  ) {
    const promise = this.tileImageLoader.getCachedTileImage(dimensionValues, props);

    if (priority == PRIORITY_CURRENT_VIEW) this.currentViewLoadingManager.register(promise);
    this.loadingManager.register(promise);

    return await promise;
  }

  private onTilesChanged({ tiles, removedTiles }: TileChangeSet) {
    this.tiles = tiles;
    removedTiles.forEach((tile) => {
      this.tileImageTaskStore.cancelPendingTasksForTile(tile);
      // FIXME: directly purging tiles results in weird behavior when panning
      // around the map... disabling for now. This does mean that the amount of
      // images kept in memory keeps growing...
      //
      //this.tileImageStore.purgeTile(tile)
    });
  }

  renderComponents() {
    return undefined;
  }
}
